import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { Link } from '@/components/form/baseInputs/Link/Link';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { Card } from '@/components/layout/Card/Card';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { DocumentType } from '@/types/schema';

import { useSupportsMultiDocSummary } from '../hooks/useSupportsMultiDocSummary';
import {
  RegenerateModalFormPaths,
  RegenerateSummaryModalFormShape,
} from './RegenerateSummaryModal.constants';
import {
  getDefaultSummaryNameForPrompt,
  getIncorporateDocumentsLabel,
  getIncorporateDocumentsSubtext,
} from './utils/RegenerateSummaryModalForm.utils';

export interface RegenerateSummaryModalFormProps {
  openedFromAddNew?: boolean;
  currentSummaryName?: string;
  summaryOptions: TypeaheadSelectInputOption<string>[];
  promptOptions: TypeaheadSelectInputOption<string>[];
  documentType: DocumentType;
}

export function RegenerateSummaryModalForm({
  openedFromAddNew,
  summaryOptions,
  promptOptions,
  currentSummaryName,
  documentType,
}: RegenerateSummaryModalFormProps) {
  const {
    control,
    formState: { dirtyFields },
    setValue,
  } = useFormContext<RegenerateSummaryModalFormShape>();
  const supportsMultiDocSummary = useSupportsMultiDocSummary(documentType);
  const canAccessSummaryPromptsAdminPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_SUMMARY_PROMPTS_ADMIN_PAGE
  );

  const regenerateAsNew = useWatch({
    name: 'regenerateAsNew',
    control,
  });
  const summaryPromptId = useWatch({
    name: 'summaryPromptId',
    control,
  });

  useEffect(() => {
    if (!regenerateAsNew || dirtyFields.summaryName) {
      return;
    }

    setValue(
      'summaryName',
      getDefaultSummaryNameForPrompt(promptOptions, summaryPromptId),
      { shouldDirty: false }
    );
  }, [
    dirtyFields.summaryName,
    promptOptions,
    regenerateAsNew,
    setValue,
    summaryPromptId,
  ]);

  return (
    <ModalFormLayout>
      <Stack spacing={2}>
        {/* If opened from add new, we don't want to show the regenerate as new checkbox, we'll just default it to true */}
        {!openedFromAddNew && (
          <FormAwareCheckbox<RegenerateSummaryModalFormShape>
            control={control}
            fieldName={
              'regenerateAsNew' as const satisfies RegenerateModalFormPaths
            }
            label="Create new summary"
            helpText="A new summary will be created"
          />
        )}
        {regenerateAsNew && (
          <FormAwareTextInput<RegenerateSummaryModalFormShape>
            control={control}
            fieldName={
              'summaryName' as const satisfies RegenerateModalFormPaths
            }
            label="Summary name"
            placeholder={
              currentSummaryName
                ? `${currentSummaryName} (copy)`
                : 'New summary name'
            }
            required
          />
        )}

        {supportsMultiDocSummary && (
          <FormAwareCheckbox<RegenerateSummaryModalFormShape>
            control={control}
            fieldName={
              'incorporateAllDocuments' as const satisfies RegenerateModalFormPaths
            }
            label={getIncorporateDocumentsLabel(documentType)}
            helpText={getIncorporateDocumentsSubtext(documentType)}
          />
        )}

        <Card variant="filled" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <FormAwareSelectInput<RegenerateSummaryModalFormShape>
              control={control}
              showEmptyValue={false}
              fieldName={
                'summaryPromptId' as const satisfies RegenerateModalFormPaths
              }
              label="Select summary prompt"
              options={promptOptions}
            />

            <FormAwareTextInput<RegenerateSummaryModalFormShape>
              control={control}
              fieldName={
                'additionalDetails' as const satisfies RegenerateModalFormPaths
              }
              label="Additional changes"
              placeholder="Optional, describe the improvements or areas of focus for this summary"
              multiline
              rows={4}
            />

            <FormAwareTypeaheadSelectInput<RegenerateSummaryModalFormShape>
              control={control}
              fieldName={
                'formatReferenceSummaryId' as const satisfies RegenerateModalFormPaths
              }
              label="Match formatting to an existing summary"
              options={summaryOptions}
              placeholder="Select a reference summary"
            />
          </Stack>
        </Card>

        {canAccessSummaryPromptsAdminPage && (
          <Callout severity="info-high">
            To manage summary prompts for all document types, go to Settings →{' '}
            <Link
              display="System prompts"
              target="_blank"
              href={getCompletePathFromRouteKey(
                ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS,
                {}
              )}
            />
          </Callout>
        )}
      </Stack>
    </ModalFormLayout>
  );
}
